import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../../context/WalletContext';
import { useContract } from '../../context/ContractContext';
import PageContainer from '../../components/layout/PageContainer/PageContainer';
import Button from '../../components/common/Button/Button';
import { shortenAddress, formatNumber, formatDate, formatUserId } from '../../utils/formatters';
import { copyToClipboard, vibrate } from '../../utils/helpers';
import { useNotification } from '../../context/NotificationContext';
import { CopyIcon, ExternalLinkIcon } from '../../assets/icons';
import { ACTIVE_NETWORK } from '../../config/networks';
import './Profile.css';

const Profile = () => {
  const navigate = useNavigate();
  const { isConnected, isInitializing, account, disconnect } = useWallet();
  const { isRegistered, isCheckingRegistration, userId, getUserInfo, getIncomeInfo, tokenBalance } = useContract();
  const { success } = useNotification();

  const [userData, setUserData] = useState(null);
  const [incomeData, setIncomeData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isInitializing || isCheckingRegistration) return;
    
    if (!isConnected) {
      navigate('/');
      return;
    }
    if (!isRegistered) {
      navigate('/register');
      return;
    }
  }, [isConnected, isRegistered, isInitializing, isCheckingRegistration, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      
      try {
        const [user, income] = await Promise.all([
          getUserInfo(),
          getIncomeInfo()
        ]);
        
        setUserData(user);
        setIncomeData(income);
      } catch (err) {
        console.error('Error fetching profile data:', err);
      }
      setLoading(false);
    };

    fetchData();
  }, [userId]);

  const handleCopyAddress = async () => {
    const copied = await copyToClipboard(account);
    if (copied) {
      vibrate(50);
      success('Address copied!');
    }
  };

  const handleDisconnect = () => {
    disconnect();
    navigate('/');
  };

  const openExplorer = () => {
    window.open(`${ACTIVE_NETWORK.blockExplorer}/address/${account}`, '_blank');
  };

  return (
    <PageContainer title="Profile">
      <div className="profile-page">
        <div className="profile-card">
          <div className="profile-avatar">
            <span className="avatar-text">{formatUserId(userId)}</span>
          </div>
          
          <div className="profile-info">
            <h2 className="profile-id">User {formatUserId(userId)}</h2>
            <div className="profile-address" onClick={handleCopyAddress}>
              <span>{shortenAddress(account, 6)}</span>
              <CopyIcon size={16} />
            </div>
          </div>

          <div className="profile-status">
            <span className={`status-badge ${userData?.active ? 'active' : 'inactive'}`}>
              {userData?.active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        <div className="profile-section">
          <h3 className="section-title">Account Details</h3>
          <div className="details-list">
            <div className="detail-item">
              <span className="detail-label">Sponsor ID</span>
              <span className="detail-value">{formatUserId(userData?.sponsorId)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Referrer ID</span>
              <span className="detail-value">{formatUserId(userData?.referrerId)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Current Package</span>
              <span className="detail-value">{formatNumber(userData?.currentPackage || 0, 0)} USDT</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">First Package</span>
              <span className="detail-value">{formatNumber(userData?.firstPackage || 0, 0)} USDT</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Total Investment</span>
              <span className="detail-value highlight">{formatNumber(userData?.totalInvestment || 0)} USDT</span>
            </div>
          </div>
        </div>

        <div className="profile-section">
          <h3 className="section-title">Income Summary</h3>
          <div className="income-grid">
            <div className="income-item">
              <span className="income-icon">📈</span>
              <span className="income-label">Total ROI</span>
              <span className="income-value">{formatNumber(incomeData?.totalROI || 0)}</span>
            </div>
            <div className="income-item">
              <span className="income-icon">⚡</span>
              <span className="income-label">Total Binary</span>
              <span className="income-value">{formatNumber(incomeData?.totalBinary || 0)}</span>
            </div>
            <div className="income-item">
              <span className="income-icon">👥</span>
              <span className="income-label">Total Referral</span>
              <span className="income-value">{formatNumber(incomeData?.totalReferral || 0)}</span>
            </div>
            <div className="income-item total">
              <span className="income-icon">💰</span>
              <span className="income-label">Total Income</span>
              <span className="income-value">{formatNumber(incomeData?.totalIncome || 0)}</span>
            </div>
          </div>
        </div>

        <div className="profile-section">
          <h3 className="section-title">Wallet</h3>
          <div className="wallet-info">
            <div className="wallet-balance">
              <span className="wallet-label">USDT Balance</span>
              <span className="wallet-value">{formatNumber(tokenBalance)}</span>
            </div>
            <button className="explorer-btn" onClick={openExplorer}>
              <ExternalLinkIcon size={16} />
              <span>View on Explorer</span>
            </button>
          </div>
        </div>

        <div className="profile-actions">
          <Button
            variant="danger"
            fullWidth
            onClick={handleDisconnect}
          >
            Disconnect Wallet
          </Button>
        </div>
      </div>
    </PageContainer>
  );
};

export default Profile;